import { NumberUtils } from '@fulcrumapp/fulcrum-core';
import _ from 'lodash';
import ExpressionEngineGlobals from './expression-engine-globals';

let nextID = 0;

export default class ExpressionEngine {
  id;
  onMessageHandler;
  globals;
  sandboxURL;
  initialized;
  callbacks;
  callbackID;
  expressions;
  form;
  delayedSends;
  holder;
  iframe;
  contentWindow;
  delegate;
  _locked;
  private static rootEngine;
  private static rootHolder;
  constructor({ holder, sandboxURL }) {
    this.onMessageHandler = this.onMessage.bind(this);
    this.globals = new ExpressionEngineGlobals();
    this.sandboxURL = sandboxURL;
    this.initialized = false;
    this.callbacks = {};
    this.callbackID = 0;
    this.expressions = null;
    this.form = null;
    this.delayedSends = [];
    this.holder = holder;
    this.id = `sandbox-${++nextID}`;
  }

  static initializeRootEngine(sandboxURL) {
    if (this.rootEngine) {
      this.rootEngine.dispose();
      this.rootEngine = null;
      this.rootHolder = null;
    }
    // @ts-ignore
    this.rootEngine = new ExpressionEngine({ sandboxURL });
    this.rootEngine.setup();
  }

  static acquire(holder, sandboxURL) {
    if (!this.rootEngine) {
      this.initializeRootEngine(sandboxURL);
    }

    if (this.rootHolder && this.rootHolder !== holder) {
      // @ts-ignore
      const engine = new ExpressionEngine({ sandboxURL });
      // @ts-ignore
      engine.setup();
      return engine;
    }

    this.rootHolder = holder;

    return this.rootEngine;
  }

  setup(callback) {
    this.iframe = document.createElement('iframe');

    window.addEventListener('message', this.onMessageHandler);

    this.iframe.sandbox = 'allow-scripts allow-same-origin';
    this.iframe.style.display = 'none';
    this.iframe.src = this.sandboxURL;
    this.iframe.id = this.id;

    document.body.appendChild(this.iframe);

    this.iframe.addEventListener('load', () => {
      if (!this.iframe) {
        return;
      }

      this.contentWindow = this.iframe.contentWindow;

      this.initialized = true;

      this.setGlobals(() => {
        for (const delayedSend of this.delayedSends) {
          // @ts-ignore
          this.send(...delayedSend);
        }

        this.delayedSends = [];

        if (callback) {
          return callback();
        }

        return null;
      });
    });
  }

  dispose() {
    if (!this.iframe) {
      return;
    }

    window.removeEventListener('message', this.onMessageHandler);

    if (this.iframe.parentNode) {
      this.iframe.remove();
    }

    this.iframe = null;
  }

  setValues(values, customVariables, featureVariables, callback) {
    const variables = {
      values: values.toJSON(),
      customVariables,
      featureVariables,
    };

    this.send('values', variables, callback);
  }

  setExpressions(expressions, callback) {
    if (this.expressions !== expressions) {
      this.expressions = expressions;
      this.send('expressions', { expressions }, callback);
      return null;
    }

    return callback();
  }

  setGlobals(callback) {
    this.send('globals', { globals: this.globals.variables }, callback);
  }

  prepare(form, callback) {
    if (this.form !== form) {
      this.form = form;
      this.send('prepare', { form }, callback);
      return null;
    }

    return callback();
  }

  evaluate(callback) {
    this.send('evaluate', {}, (message) => {
      const { results } = message;

      for (const result of results) {
        // TODO(zhm) this needs to be compared against the mobile app code
        // TODO(zhm) also this data needs to be HTML sanitized/escaped
        if (_.isNumber(result.value)) {
          result.value = NumberUtils.formatMachine(result.value);
        } else if (!_.isString(result.value)) {
          result.value = null;
        }
      }

      callback(results);
    });
  }

  triggerDataEvent(event, callback) {
    this.send('triggerDataEvent', { event }, (message) => {
      const { results } = message;

      for (const result of results) {
        if (result.value != null) {
          result.value = JSON.parse(result.value);
        }

        // TODO(zhm) this needs to be compared against the mobile app code
        // TODO(zhm) also this data needs to be HTML sanitized/escaped
        if (_.isNumber(result.value)) {
          result.value = NumberUtils.formatMachine(result.value);
        }
      }

      callback(results);
    });
  }

  finishAsyncCallback(callbackID, callbackArguments) {
    this.send('finishAsyncCallback', { callbackID, callbackArguments }, (message) => {
      const { results } = message;

      for (const result of results) {
        if (result.value != null) {
          result.value = JSON.parse(result.value);
        }

        // TODO(zhm) this needs to be compared against the mobile app code
        // TODO(zhm) also this data needs to be HTML sanitized/escaped
        if (_.isNumber(result.value)) {
          result.value = NumberUtils.formatMachine(result.value);
        }
      }

      if (this.delegate) {
        this.delegate.onProcessAsyncExpressionResults(results);
      } else {
        throw new Error('no delegate to process async expression results');
      }
    });
  }

  onMessage(event) {
    if (event.data?.extensionMessage) {
      if (this.delegate) {
        this.delegate.onProcessExtensionMessage(event.data.extensionMessage);
      }

      return null;
    }

    if (event.source !== this.contentWindow || typeof event.data !== 'string') {
      return null;
    }

    let message: any = null;

    try {
      message = JSON.parse(event.data);
    // eslint-disable-next-line no-empty
    } catch (e) {
      return null;
    }

    if (!message) {
      return null;
    }

    const [sandboxID, callbackID] = message.id.split('/');

    if (sandboxID !== this.id) {
      return null;
    }

    // for host function calls we pass control back to editor so it has a chance to
    // to setup callbacks in response to the host function call.
    if (message.name === 'hostFunction') {
      this.executeHostFunctionCall(message);
      return null;
    }

    const callback = this.callbacks[callbackID];

    delete this.callbacks[callbackID];

    if (callback) {
      return callback(message);
    }

    throw new Error(`No callback found for id ${message.id}`);
  }

  send(name, object, callback) {
    // If an attempt is made to send the iframe a message before it's ready, queue the
    // message to be sent once the frame is fully loaded.
    if (!this.initialized) {
      this.delayedSends.push([name, object, callback]);
      return;
    }

    const callbackID = ++this.callbackID;

    const message = {
      ...object,
      name,
      id: [this.id, callbackID].join('/'),
    };

    this.callbacks[callbackID] = callback;

    this.postMessage(JSON.stringify(message));
  }

  postMessage(message) {
    this.contentWindow.postMessage(message, this.sandboxURL);
  }

  executeHostFunctionCall(message) {
    this.globals[message.functionName](this, ...message.argumentList);
  }

  evaluateExpressions(form, expressions, variables, valuesForConditions, callback) {
    this.lock(() => {
      this.prepare(form, () => {
        this.setExpressions(expressions, () => {
          this.setValues(valuesForConditions, null, variables, () => {
            this.evaluate((results) => {
              this.unlock();
              callback(results);
            });
          });
        });
      });
    });
  }

  evaluateDataEvent(form, valuesForConditions, variables, event, callback) {
    this.lock(() => {
      this.prepare(form, () => {
        this.setValues(valuesForConditions, null, variables, () => {
          this.triggerDataEvent(event, (results) => {
            this.unlock();
            callback(results);
          });
        });
      });
    });
  }

  get isLocked() {
    return this._locked;
  }

  lock(handler) {
    if (this.isLocked) {
      setTimeout(() => {
        this.lock(handler);
      }, 16 /* wait about a frame */);

      return;
    }

    this._locked = true;

    handler();
  }

  unlock() {
    this._locked = false;
  }
}
